import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, Stack, FormControl, InputLabel, Select, MenuItem,
  Checkbox, ListItemText, CssBaseline, Tooltip, useMediaQuery, useTheme,
} from '@mui/material';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import dataStore from '../dataStore';
import { useLogoMaps } from '../useLogoMaps';

// ══════════════════════════════════════════════════════════
//  SURVIVOR GRID
//
//  Rows: every NFL team. Columns: remaining contest legs (past
//  weeks drop off automatically). Cells: opponent + win prob from
//  Caesars lookahead moneylines (sole full-season source), with the
//  greedy
//  optimal path (one pick per leg, each team used once, restricted
//  to teams the user still has available) is highlighted, and its
//  cumulative advance probability is shown as Survival Rate.
// ══════════════════════════════════════════════════════════

const THROTTLE_MS = 5000; // lookahead lines move slowly
const SOURCE_BOOK = 'Caesars'; // only book with full-season lookahead MLs

const CONTEST_FORMATS = {
  circa: { label: 'Circa' },                                        // Weeks 1–18 + TG + Xmas legs, 1 pick each (20 picks)
  splash: { label: 'Splash', doubleWeeks: [9, 12, 13, 14, 15, 16] }, // Weeks 1–18; 2 picks in weeks 9, 12–16 (24 picks)
};

// Smooth red→green gradient over the practical NFL win-prob range (20–80%);
// values outside clamp to the ends. The printed number carries precision —
// color is just for scanning.
const winColor = (p) => {
  if (p == null) return '#ffffff';
  const t = Math.max(0, Math.min(1, (p - 0.20) / 0.60));
  return `hsl(${Math.round(120 * t)}, 90%, 55%)`;
};

const customTheme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: '#141414', paper: '#141414' },
    text: { primary: '#ffffff' },
  },
  typography: { fontFamily: '"Roboto Mono", monospace' },
});

// start_date is UTC; shift to ET so US night games keep their US calendar day;
// reads on the shifted date use UTC getters.
const toEt = (d) => new Date(d.getTime() - 6 * 3600 * 1000);

// Circa holiday legs (Circa Survivor rules): Thanksgiving leg spans Wed–Fri
// around US Thanksgiving (4th Thursday of Nov); Christmas leg spans Dec 24–25.
const isTgLeg = (d) => {
  const e = toEt(d);
  if (e.getUTCMonth() !== 10) return false;
  // 4th Thursday of this November
  const first = new Date(Date.UTC(e.getUTCFullYear(), 10, 1));
  const firstThu = 1 + ((4 - first.getUTCDay() + 7) % 7);
  const tgDate = firstThu + 21;
  return e.getUTCDate() >= tgDate - 1 && e.getUTCDate() <= tgDate + 1;
};
const isXmasLeg = (d) => { const e = toEt(d); return e.getUTCMonth() === 11 && (e.getUTCDate() === 24 || e.getUTCDate() === 25); };

export default function Survivor() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { teamMap } = useLogoMaps();

  const [format, setFormat] = useState(() => localStorage.getItem('survivorFormat') || 'splash');
  useEffect(() => { localStorage.setItem('survivorFormat', format); }, [format]);

  const [pickMode, setPickMode] = useState(() => localStorage.getItem('survivorPickMode') || 'optimal');
  useEffect(() => { localStorage.setItem('survivorPickMode', pickMode); }, [pickMode]);

  // Custom picks: legKey → array of teamIds (seeded from optimal on first switch)
  const [customPicks, setCustomPicks] = useState(() => {
    try { return JSON.parse(localStorage.getItem('survivorCustomPicks')) || {}; } catch (e) { return {}; }
  });
  useEffect(() => { localStorage.setItem('survivorCustomPicks', JSON.stringify(customPicks)); }, [customPicks]);

  // Teams the user still has available (empty array = all available)
  const [unavailable, setUnavailable] = useState(() => {
    try { return JSON.parse(localStorage.getItem('survivorUnavailable')) || []; } catch (e) { return []; }
  });
  useEffect(() => { localStorage.setItem('survivorUnavailable', JSON.stringify(unavailable)); }, [unavailable]);

  // ── Throttled odds subscription ──
  const readBets = useCallback(() => {
    const data = dataStore.get('odds');
    const arr = data instanceof Map ? Array.from(data.values()) : (data || []);
    return arr.filter(b =>
      b && !b.pickem && b.league === 'NFL' && !b.isLive
      && (b.marketName === 'Moneyline' || b.betTypeDisplay === 'Moneyline')
      && b.marketCategory === 'Game Line'
    );
  }, []);
  const [bets, setBets] = useState(readBets);
  const timerRef = useRef(null);
  useEffect(() => {
    const unsub = dataStore.subscribe('odds', () => {
      if (timerRef.current) return;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setBets(readBets());
      }, THROTTLE_MS);
    });
    return () => { if (timerRef.current) clearTimeout(timerRef.current); unsub && unsub(); };
  }, [readBets]);

  // ── Build fixtures → legs → grid → optimal path ──
  const { teams, legs, grid, optimal } = useMemo(() => {
    const now = new Date();
    const fixtures = new Map();
    bets.forEach(b => {
      const key = b.fixture_id || b.eventName;
      if (!key || !b.eventStart) return;
      const start = new Date(b.eventStart);
      if (start <= now) return; // past games (and finished weeks) drop off
      if (!fixtures.has(key)) {
        fixtures.set(key, {
          start,
          awayTeamId: b.awayTeamId, homeTeamId: b.homeTeamId,
          away: null, home: null, // Caesars price per side
        });
      }
      const fx = fixtures.get(key);
      if (b.bookName !== SOURCE_BOOK) return;
      if (b.side === 'Away') fx.away = b.price;
      else if (b.side === 'Home') fx.home = b.price;
    });

    if (fixtures.size === 0) return { teams: [], legs: [], grid: new Map(), optimal: new Map(), survival: null };

    const impliedProb = (price) => {
      const p = parseFloat(price);
      if (!p) return null;
      return p > 0 ? 100 / (p + 100) : -p / (-p + 100);
    };
    const awayWinProb = (fx) => {
      const a = impliedProb(fx.away);
      const h = impliedProb(fx.home);
      if (a != null && h != null && a + h > 0) return a / (a + h);
      if (a != null) return a;
      if (h != null) return 1 - h;
      return null;
    };

    // Week bucketing on the ET-shifted date, Tuesday boundary
    const starts = Array.from(fixtures.values()).map(f => f.start).sort((a, b) => a - b);
    const anchor = toEt(starts[0]);
    anchor.setUTCHours(0, 0, 0, 0);
    while (anchor.getUTCDay() !== 2) anchor.setUTCDate(anchor.getUTCDate() - 1);
    const weekOf = (d) => Math.floor((toEt(d) - anchor) / (7 * 24 * 3600 * 1000)) + 1;

    const legSet = new Map();
    const doubleWeeks = new Set(CONTEST_FORMATS[format].doubleWeeks || []);
    const legOf = (d) => {
      if (format === 'circa' && isTgLeg(d)) return { key: 'TG', label: 'TG', order: weekOf(d) + 0.4, picks: 1 };
      if (format === 'circa' && isXmasLeg(d)) return { key: 'XMAS', label: 'Xmas', order: weekOf(d) + 0.4, picks: 1 };
      const w = weekOf(d);
      const picks = doubleWeeks.has(w) ? 2 : 1;
      return { key: `W${w}`, label: picks === 2 ? `${w} ×2` : `${w}`, order: w, picks };
    };

    const grid = new Map();
    const teamIds = new Set();
    fixtures.forEach(fx => {
      if (!fx.awayTeamId || !fx.homeTeamId) return;
      const leg = legOf(fx.start);
      if (!legSet.has(leg.key)) legSet.set(leg.key, leg);
      const pAway = awayWinProb(fx);
      teamIds.add(fx.awayTeamId); teamIds.add(fx.homeTeamId);
      if (!grid.has(fx.awayTeamId)) grid.set(fx.awayTeamId, new Map());
      if (!grid.has(fx.homeTeamId)) grid.set(fx.homeTeamId, new Map());
      grid.get(fx.awayTeamId).set(leg.key, { oppId: fx.homeTeamId, at: true, win: pAway, start: fx.start });
      grid.get(fx.homeTeamId).set(leg.key, { oppId: fx.awayTeamId, at: false, win: pAway != null ? 1 - pAway : null, start: fx.start });
    });

    const legs = Array.from(legSet.values()).sort((a, b) => a.order - b.order);
    const teams = Array.from(teamIds)
      .map(id => ({ id, abbr: teamMap[id]?.abbreviation || '?', name: teamMap[id]?.name || id, logo: teamMap[id]?.logo || '' }))
      .sort((a, b) => a.abbr.localeCompare(b.abbr));

    // Greedy optimal path: legs in order, best available unused team each leg
    const blocked = new Set(unavailable);
    const used = new Set();
    const optimal = new Map(); // legKey → teamId
    let survival = 1;
    let picks = 0;
    legs.forEach(leg => {
      const candidates = [];
      grid.forEach((cells, teamId) => {
        if (blocked.has(teamId) || used.has(teamId)) return;
        const c = cells.get(leg.key);
        if (c && c.win != null) candidates.push({ teamId, win: c.win });
      });
      candidates.sort((a, b) => b.win - a.win);
      const take = candidates.slice(0, leg.picks || 1);
      if (take.length) {
        optimal.set(leg.key, new Set(take.map(t => t.teamId)));
        take.forEach(t => { used.add(t.teamId); survival *= t.win; });
        picks += take.length;
      }
    });
    if (picks === 0) survival = null;

    return { teams, legs, grid, optimal, survival };
  }, [bets, teamMap, format, unavailable]);

  // Effective picks for display: optimal (computed) or the user's custom set.
  const { picksByLeg, effSurvival, incompleteLegs } = useMemo(() => {
    const picksByLeg = new Map();
    const incompleteLegs = new Set();
    let prod = 1, any = false;
    legs.forEach(leg => {
      let ids;
      if (pickMode === 'optimal') {
        ids = Array.from(optimal.get(leg.key) || []);
      } else {
        ids = (customPicks[leg.key] || []).filter(id => grid.get(id)?.get(leg.key)?.win != null && !unavailable.includes(id));
      }
      picksByLeg.set(leg.key, new Set(ids));
      if (ids.length < (leg.picks || 1)) incompleteLegs.add(leg.key);
      ids.forEach(id => {
        const w = grid.get(id)?.get(leg.key)?.win;
        if (w != null) { prod *= w; any = true; }
      });
    });
    return { picksByLeg, effSurvival: any ? prod : null, incompleteLegs };
  }, [legs, grid, optimal, pickMode, customPicks, unavailable]);

  // Custom-mode cell click: toggle pick; stealing a team from another leg
  // clears it there (that leg then shows as needing a pick).
  const handleCellClick = useCallback((legKey, teamId, legPickCap) => {
    if (pickMode !== 'custom') return;
    setCustomPicks(prev => {
      const next = {};
      Object.keys(prev).forEach(k => { next[k] = prev[k].filter(t => t !== teamId || k === legKey); });
      const arr = [...(next[legKey] || [])];
      const idx = arr.indexOf(teamId);
      if (idx >= 0) {
        arr.splice(idx, 1); // deselect
      } else {
        if (arr.length >= legPickCap) arr.shift(); // replace oldest when full
        arr.push(teamId);
      }
      next[legKey] = arr;
      return next;
    });
  }, [pickMode]);

  // Seed custom from optimal the first time the user switches over
  const handleModeChange = useCallback((mode) => {
    if (mode === 'custom' && Object.keys(customPicks).length === 0) {
      const seed = {};
      optimal.forEach((ids, legKey) => { seed[legKey] = Array.from(ids); });
      setCustomPicks(seed);
    }
    setPickMode(mode);
  }, [customPicks, optimal]);

  const cellSx = {
    border: '1px solid rgba(255,255,255,0.15)', padding: '4px 6px',
    textAlign: 'center', whiteSpace: 'nowrap', fontSize: '0.75rem', minWidth: 78,
    position: 'relative',
  };

  const teamOptions = teams;

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden', width: '100%' }}>
          <Box className="filter-section" sx={{ padding: isMobile ? '8px' : '16px', backgroundColor: 'background.default' }}>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={2} alignItems={isMobile ? 'stretch' : 'center'}>
              <FormControl variant="outlined" size="small" sx={{ minWidth: 130 }}>
                <InputLabel id="survivor-format-label" shrink sx={{ color: '#ffffff' }}>Contest</InputLabel>
                <Select labelId="survivor-format-label" label="Contest" notched value={format} onChange={(e) => setFormat(e.target.value)}>
                  {Object.entries(CONTEST_FORMATS).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="survivor-picks-label" shrink sx={{ color: '#ffffff' }}>Picks</InputLabel>
                <Select labelId="survivor-picks-label" label="Picks" notched value={pickMode} onChange={(e) => handleModeChange(e.target.value)}>
                  <MenuItem value="optimal">Optimal</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" sx={{ minWidth: 170 }}>
                <InputLabel id="survivor-teams-label" shrink sx={{ color: '#ffffff' }}>Available Teams</InputLabel>
                <Select
                  labelId="survivor-teams-label" label="Available Teams" notched multiple
                  value={teamOptions.filter(t => !unavailable.includes(t.id)).map(t => t.id)}
                  onChange={(e) => {
                    const selected = new Set(e.target.value);
                    setUnavailable(teamOptions.filter(t => !selected.has(t.id)).map(t => t.id));
                  }}
                  renderValue={(sel) => sel.length === teamOptions.length ? 'All' : `${sel.length} of ${teamOptions.length}`}
                >
                  {teamOptions.map(t => (
                    <MenuItem key={t.id} value={t.id}>
                      <Checkbox checked={!unavailable.includes(t.id)} />
                      <ListItemText primary={t.abbr} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box>
                <Tooltip title="Chance of surviving every remaining leg with the highlighted picks — the product of their win probabilities. In Custom mode, click cells to set your own picks.">
                  <Typography sx={{ fontSize: '0.75rem', color: '#BBBBBB' }}>Survival Rate</Typography>
                </Tooltip>
                <Typography sx={{ fontSize: '1.1rem', color: effSurvival == null ? '#777777' : incompleteLegs.size > 0 ? '#ff9100' : '#00c853', fontWeight: 600 }}>
                  {effSurvival != null
                    ? `${(effSurvival * 100).toFixed(effSurvival >= 0.1 ? 1 : effSurvival >= 0.01 ? 2 : 3)}%${incompleteLegs.size > 0 ? ' *' : ''}`
                    : '—'}
                </Typography>
                {incompleteLegs.size > 0 && (
                  <Typography sx={{ fontSize: '0.65rem', color: '#ff9100' }}>
                    {incompleteLegs.size} week{incompleteLegs.size > 1 ? 's' : ''} need a pick
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', px: isMobile ? 1 : 2, pb: 2 }}>
            {teams.length === 0 ? (
              <Typography sx={{ color: '#BBBBBB', mt: 4, textAlign: 'center' }}>
                Waiting for NFL moneyline data…
              </Typography>
            ) : (
              <table style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...cellSx, position: 'sticky', left: 0, top: 0, zIndex: 3, backgroundColor: '#141414', textAlign: 'left', minWidth: 110 }}>Team</th>
                    {legs.map(leg => (
                      <th key={leg.key} style={{ ...cellSx, position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#141414', color: incompleteLegs.has(leg.key) ? '#ff9100' : '#ffffff' }}>
                        {leg.label}{incompleteLegs.has(leg.key) ? ' !' : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {teams.map(team => {
                    const isOut = unavailable.includes(team.id);
                    return (
                      <tr key={team.id} style={isOut ? { opacity: 0.35 } : undefined}>
                        <td style={{ ...cellSx, position: 'sticky', left: 0, zIndex: 1, backgroundColor: '#141414', textAlign: 'left' }}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            {team.logo && <img src={team.logo} alt="" style={{ width: 18, height: 18 }} />}
                            <span>{team.abbr}</span>
                          </Stack>
                        </td>
                        {legs.map(leg => {
                          const cell = grid.get(team.id)?.get(leg.key);
                          if (!cell) {
                            return <td key={leg.key} style={{ ...cellSx, color: '#555555' }}>—</td>;
                          }
                          const opp = teamMap[cell.oppId]?.abbreviation || '?';
                          const pct = cell.win != null ? `${(cell.win * 100).toFixed(0)}%` : '—';
                          const isPick = picksByLeg.get(leg.key)?.has(team.id);
                          return (
                            <td key={leg.key}
                              onClick={() => handleCellClick(leg.key, team.id, leg.picks || 1)}
                              style={{
                                ...cellSx,
                                ...(isPick ? { outline: '2px solid #00e5ff', outlineOffset: '-2px' } : {}),
                                ...(pickMode === 'custom' && !isOut ? { cursor: 'pointer' } : {}),
                              }}>
                              <Tooltip title={cell.start.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}>
                                <span>
                                  <span style={{ color: '#BBBBBB' }}>{cell.at ? '@' : 'vs'} {opp}</span>
                                  <br />
                                  <span style={{ color: winColor(cell.win), fontWeight: 600 }}>{pct}</span>
                                </span>
                              </Tooltip>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Box>
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
}